import classes from '@/containers/pages/commonSettings/tabs/StoragePage/styles.module.css';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Progress, Stack, Text } from '@mantine/core';
import { IUsePopUpUI } from '@components/UI/PopUp';
import { storageConfigurationApiArea } from '@/services/api/storageConfigurationApiArea';
import { IMigrationStatus, UserTaskStatus } from '@/services/api/storageConfigurationApiArea/types';
import { IconAlertCircle } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';

type MigrationProgressDialogProps = {
  storageId: number; // ID хранилища (для получения статуса)
  fromStorageId: number; // ID хранилища-источника (для повтора)
  toStorageId: number; // ID хранилища-назначения (для повтора)
  onSuccess?: () => void;
  onCancel?: () => void;
  onProgressUpdate?: (percent: number) => void;
  onRetry?: (newMigrationId: number) => void; // Callback при повторе миграции
} & IUsePopUpUI;

const MigrationProgressDialog = (props: MigrationProgressDialogProps) => {
  const { t } = useTranslation();
  const {
    storageId,
    fromStorageId,
    toStorageId,
    onSuccess,
    onCancel,
    onProgressUpdate,
    onRetry,
    handleClosePopUp
  } = props;
  const [migrationData, setMigrationData] = useState<IMigrationStatus | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    const fetchMigrationStatus = async () => {
      try {
        // Передаем ID хранилища для получения статуса миграции
        const data = await storageConfigurationApiArea().GetMigrationStatus({
          id: storageId
        });
        setMigrationData(data);

        // Обновляем прогресс в родительском компоненте
        if (onProgressUpdate) {
          onProgressUpdate(data.Percent);
        }

        // Если миграция успешно завершена, закрываем диалог
        if (data.Status === UserTaskStatus.Done || data.Percent >= 100) {
          setTimeout(() => {
            if (onSuccess) {
              onSuccess();
            }
            handleClosePopUp();
          }, 1000);
        }
      } catch (error) {
        // Игнорируем ошибку
      }
    };

    // Запускаем проверку статуса каждые 3 секунды
    const interval = setInterval(fetchMigrationStatus, 3000);

    // Первый запрос сразу
    fetchMigrationStatus();

    return () => clearInterval(interval);
  }, [storageId, onSuccess, onProgressUpdate, handleClosePopUp]);

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      // Используем ID задачи миграции для отмены
      await storageConfigurationApiArea().CancelMigration({
        id: storageId
      });

      showNotification({
        title: t('storagePage.notifications.migrationCancelled'),
        message: '',
        autoClose: true,
        color: 'blue',
        style: { zIndex: 1003 }
      });

      if (onCancel) {
        await onCancel();
      }
      handleClosePopUp();
    } catch (error) {
      showNotification({
        title: t('storagePage.notifications.migrationCancelError'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const percent = migrationData?.Percent || 0;
  const hasError =
    migrationData?.Status === UserTaskStatus.Error ||
    migrationData?.Status === UserTaskStatus.Canceled ||
    migrationData?.Status === UserTaskStatus.DoneWithErrors;

  // Проверяем, доступна ли кнопка retry (есть ли корректные ID хранилищ)
  const canRetry = fromStorageId && toStorageId && toStorageId !== 0;

  const handleRetry = async () => {
    try {
      setIsRetrying(true);

      // Проверяем, что у нас есть корректные ID хранилищ
      if (!fromStorageId || !toStorageId || toStorageId === 0) {
        showNotification({
          title: t('storagePage.notifications.migrationRetryError'),
          message: t('storagePage.notifications.invalidStorageIds'),
          autoClose: true,
          color: 'red',
          style: { zIndex: 1003 }
        });
        return;
      }

      // Запускаем новую миграцию
      const newMigrationData = await storageConfigurationApiArea().MigrateStorage({
        from: fromStorageId,
        to: toStorageId
      });

      showNotification({
        title: t('storagePage.notifications.migrationRetryStarted'),
        message: '',
        autoClose: true,
        color: 'blue',
        style: { zIndex: 1003 }
      });

      // Уведомляем родителя о новой миграции
      if (onRetry) {
        onRetry(newMigrationData.TaskId);
      }

      // Закрываем диалог - новая миграция отобразится автоматически
      handleClosePopUp();
    } catch (error) {
      showNotification({
        title: t('storagePage.notifications.migrationRetryError'),
        message: '',
        autoClose: true,
        color: 'red',
        style: { zIndex: 1003 }
      });
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className={classes.processDialog}>
      <div className={classes.form}>
        <Stack gap="lg">
          {hasError ? (
            <>
              <Alert icon={<IconAlertCircle size="1rem" />} color="red" variant="light">
                {t('storagePage.migrationProgressError')}
              </Alert>
              <Progress value={100} size="md" radius="xs" color="red" />
            </>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                {t('storagePage.migrationProgress.description')}
              </Text>
              <Stack gap="xs">
                <Progress value={percent} size="md" radius="xs" />
                <Text size="sm" ta="center" fw={500}>
                  {Math.round(percent)}%
                </Text>
              </Stack>
            </>
          )}
        </Stack>
      </div>
      <Button
        fullWidth
        onClick={handleCancel}
        variant="light"
        disabled={isCancelling}
        loading={isCancelling}
      >
        {t('storagePage.migrationProgress.cancelButton')}
      </Button>
      {hasError && (
        <div className={classes.actions}>
          <Button fullWidth onClick={handleClosePopUp} variant="light" color="gray">
            {t('storagePage.migrationProgressCancel')}
          </Button>
          <Button
            fullWidth
            onClick={handleRetry}
            variant="light"
            disabled={isRetrying || !canRetry}
            loading={isRetrying}
          >
            {t('storagePage.migrationProgressRetry')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default MigrationProgressDialog;
